import React, {FunctionComponent, useCallback, useLayoutEffect, useState} from "react";
import {Dialog, DialogContent, DialogTitle, Theme, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import PurchasePlanningInformationImportDialogFileImport from "./import-via-excel";
import PurchasePlanningInformationImportDialogPrefixImport from "./import-via-prefix";
import {VendorDistribution} from "../planning-draft";

export interface PurchasePlanningInformationImportDialogDataEntry {
    partNo: string;
    quantity: number;
    note?: string;
    forecastedQty?: number;
    vendorDistributions?: VendorDistribution[];
}


export interface PurchasePlanningInformationImportDialogProps {
    open: boolean;
    close: () => void;
    onImport: (data: PurchasePlanningInformationImportDialogDataEntry[]) => void | Promise<void>;
}

const PurchasePlanningInformationImportDialog: FunctionComponent<PurchasePlanningInformationImportDialogProps> = (props) => {
    const [tab, setTab] = useState<'file' | 'prefix'>('file');

    /**
     * State for the import CSV file component as soon as the dialog is opened.
     */
    useLayoutEffect(() => {
        setTab('file');
    }, [props.open])


    /**
     * The sx style mixin for the condition's toggle button group.
     */
    const toggleButtonGroupMixin = useCallback<ThemeMixin<Theme>>((theme) => {
        return ({
            width: '100%',
            border: `1px solid rgba(var(--palette-common-onBackgroundChannel) / 0.23)`,
            '& .MuiToggleButton-root': {
                backgroundColor: 'transparent',
                borderColor: 'transparent !important',
                color: theme.palette.text.primary,
                height: 30,
                width: 'calc(50% - 4px)',
                marginBlock: 0.5,
                marginInline: 0.5,
                padding: 1,
            },
            '& .MuiToggleButton-root:hover': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.text.primary,
            },
            '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.primary.main,
            },
            '& .MuiToggleButton-root.Mui-selected:hover': {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
            },
        })
    }, []);

    return (
        <>
            <Dialog
                open={props.open}
                onClose={props.close}
                maxWidth={tab === 'file' ? 'sm' : 'md'}
                fullWidth
            >
                <DialogTitle fontWeight={600}>
                    Import Records to Purchase Planning
                </DialogTitle>
                <DialogContent sx={{height: 50, p: 0, minHeight: 50, marginInline: 'auto', width: '100%', paddingInline: 3}}>
                    <ToggleButtonGroup
                        orientation="horizontal"
                        value={tab}
                        exclusive
                        onChange={(e, value) => setTab(value)}
                        sx={toggleButtonGroupMixin}
                    >
                        <ToggleButton value={'file'}>
                            <Typography variant={'overline'} fontWeight={600}>
                                Import via File
                            </Typography>
                        </ToggleButton>
                        <ToggleButton value={'prefix'}>
                            <Typography variant={'overline'} fontWeight={600}>
                                Import via Prefix
                            </Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </DialogContent>
                {
                    tab === 'file' &&
                    <PurchasePlanningInformationImportDialogFileImport onImport={props.onImport} closeDialog={props.close}/>
                }
                {
                    tab === 'prefix' &&
                    <PurchasePlanningInformationImportDialogPrefixImport onImport={props.onImport} closeDialog={props.close}/>
                }
            </Dialog>
        </>
    );
}

export default PurchasePlanningInformationImportDialog;